import React from 'react'
import { CheckCircle, Clock, Loader2 } from 'lucide-react'

interface AnalysisStep {
  id: string
  title: string
  description: string
  status: 'pending' | 'active' | 'completed' | 'error'
}

interface AnalysisProgressProps {
  steps: AnalysisStep[]
  currentStep: string
  analysisType?: 'causal-chain' | 'pros-cons' | 'context' | 'multi-option'
}

export const AnalysisProgress: React.FC<AnalysisProgressProps> = ({ steps, currentStep, analysisType = 'pros-cons' }) => {
  const getAnalysisTitle = () => {
    switch (analysisType) {
      case 'causal-chain':
        return 'Causal Chain Analysis in Progress'
      case 'pros-cons':
        return 'Pros & Cons Analysis in Progress'
      case 'context':
        return 'Context Analysis in Progress'
      case 'multi-option':
        return 'Multi-Option Analysis in Progress'
      default:
        return 'AI Analysis in Progress'
    }
  }

  const getAnalysisDescription = () => {
    switch (analysisType) {
      case 'causal-chain':
        return 'Our AI is mapping out the causal relationships between your decision and its potential outcomes, showing how each choice leads to different results.'
      case 'pros-cons':
        return 'Our AI is evaluating each option based on your criteria, weighing the pros and cons to provide a comprehensive analysis.'
      case 'context':
        return 'Our AI is analyzing your decision within your personal context, considering your background, preferences, and circumstances.'
      case 'multi-option':
        return 'Our AI is comparing multiple options across different criteria to find the best solution for your decision.'
      default:
        return 'Our AI is analyzing your decision to provide insights and recommendations.'
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
        <h3 className="text-lg font-medium text-gray-900">{getAnalysisTitle()}</h3>
      </div>
      
      <p className="text-gray-600 mb-6">{getAnalysisDescription()}</p>
      
      <div className="space-y-4">
        {steps.map((step) => {
          const isActive = step.id === currentStep
          const isCompleted = step.status === 'completed'
          
          return (
            <div key={step.id} className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                {isActive ? (
                  <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
                ) : isCompleted ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Clock className="w-5 h-5 text-gray-400" />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <h4 className={`text-sm font-medium ${
                    isActive ? 'text-primary-900' : 
                    isCompleted ? 'text-green-900' : 
                    'text-gray-500'
                  }`}>
                    {step.title}
                  </h4>
                  {isActive && (
                    <span className="px-2 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                      In Progress
                    </span>
                  )}
                </div>
                <p className={`text-sm mt-1 ${
                  isActive ? 'text-primary-700' : 
                  isCompleted ? 'text-green-700' : 
                  'text-gray-500'
                }`}>
                  {step.description}
                </p>
              </div>
            </div>
          )
        })}
      </div>
      
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Analysis Progress</span>
          <span>
            {steps.filter(s => s.status === 'completed').length} of {steps.length} steps completed
            {steps.some(s => s.status === 'active') && ' (1 in progress)'}
          </span>
        </div>
        <div className="mt-2 bg-gray-200 rounded-full h-2">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ 
              width: `${Math.min((steps.filter(s => s.status === 'completed').length / steps.length) * 100, 100)}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  )
} 